import React, { useState, useEffect } from 'react'
import {
  Card,
  Form,
  Input,
  Button,
  Row,
  Col,
  Select,
  DatePicker,
  InputNumber,
  message,
  Steps,
  Tag,
  Space,
  Alert,
} from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { AgreementTemplate, ProposalStatus } from '@/types'
import { agreementStore } from './agreementStore'
import { useMasterData } from '@/context/MasterDataContext'
import { proposalStore } from '../Proposal/proposalStore'
import { leadStore } from '../Lead/leadStore'
import { generateCode } from '@/utils/codeGenerator'
import DocumentUploader from '@/components/DocumentUploader'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'

interface ProposalOption {
  value: string
  label: string
  customerName: string
  leadId: string
  tariff: string
  location: string
}

// For every selected agreement type, capture the real uploaded file (if any)
// — `values.signedCopies[type]` is a real bucket URL from DocumentUploader,
// not a browser-local fileList. Types with no upload are dropped, so the
// result length === uploaded count.
const collectSignedCopies = (values: any, types: string[], existingSignedCopies: any[] = []) =>
  types
    .map((t) => {
      const existingMatch = existingSignedCopies.find((c) => c.type === t)
      const url: string | undefined = values.signedCopies?.[t] || existingMatch?.url || existingMatch?.fileUrl
      const fileName = url ? decodeURIComponent(url.split('/').pop() || '') : (existingMatch?.fileName || '')
      return { type: t, fileName, url }
    })
    .filter((c) => c.url)

// Real proposals (from the store) drive the dropdown + auto-fill. Only APPROVED or WON proposals are allowed.
const buildProposalOptions = (): ProposalOption[] =>
  proposalStore
    .getAll()
    .filter((p) => p.status === ProposalStatus.APPROVED || p.status === ProposalStatus.WON)
    .map((p) => {
      const lead = leadStore.getLead(p.leadId)
      return {
        value: p.id,
        label: `${p.referenceNumber || p.id} — ${p.leadName || lead?.companyName || 'Proposal'}`,
        customerName: p.leadName || lead?.companyName || '',
        leadId: p.leadId,
        tariff: `₹${p.sakthiTariff || p.deliveredTariff || p.tariff || 0}/kWh`,
        location: lead?.city || '—',
      }
    })

const AgreementForm: React.FC = () => {
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = useParams()
  const isEditing = !!id

  const { agreementTypes, generators, consumers } = useMasterData()
  const agreementTypeOptions = agreementTypes
    .filter(a => a.status === 'active')
    .map(a => ({ value: a.name, label: `${a.code} — ${a.name}` }))

  // Hydrate stores from backend API on mount
  const [proposals, setProposals] = useState(() => proposalStore.getAll())
  const [, setAgreements] = useState(() => agreementStore.getAll())

  useEffect(() => {
    const update = () => {
      setProposals(proposalStore.getAll())
      setAgreements(agreementStore.getAll())
    }
    update()
    proposalStore.refresh().then(update)
    agreementStore.refresh().then(update)
    leadStore.refresh().then(update)

    window.addEventListener(proposalStore.eventName, update)
    window.addEventListener(agreementStore.eventName, update)
    window.addEventListener(leadStore.eventName, update)

    return () => {
      window.removeEventListener(proposalStore.eventName, update)
      window.removeEventListener(agreementStore.eventName, update)
      window.removeEventListener(leadStore.eventName, update)
    }
  }, [])

  const proposalOptions: ProposalOption[] = proposals
    .filter((p) => p.status === ProposalStatus.APPROVED || p.status === ProposalStatus.WON)
    .map((p) => {
      const lead = leadStore.getLead(p.leadId)
      return {
        value: p.id,
        label: `${p.referenceNumber || p.id} — ${p.leadName || lead?.companyName || 'Proposal'}`,
        customerName: p.leadName || lead?.companyName || '',
        leadId: p.leadId,
        tariff: `₹${p.sakthiTariff || p.deliveredTariff || p.tariff || 0}/kWh`,
        location: lead?.city || '—',
      }
    })

  const [currentStep, setCurrentStep] = useState(0)
  const [selectedProposal, setSelectedProposal] = useState<string | null>(
    (location.state as any)?.proposalId ?? null
  )
  const [startDate, setStartDate] = useState<Dayjs | null>(null)
  const [endDate, setEndDate] = useState<Dayjs | null>(null)

  // Stamp Paper Value: ₹100/200/500 cover most cases, but stamp duty varies
  // by state and document value, so whatever isn't a preset must still be
  // enterable — there's no master list this is meant to be picked from.
  // Typing a number adds it as a selectable option (kept for this session so
  // re-opening the dropdown still shows it), instead of the value silently
  // having nowhere to go because it isn't one of the three fixed options.
  const [stampPaperOptions, setStampPaperOptions] = useState([
    { label: '₹100', value: 100 },
    { label: '₹200', value: 200 },
    { label: '₹500', value: 500 },
  ])
  const [stampPaperSearch, setStampPaperSearch] = useState('')

  // Agreement types picked in Step 1 — one signed-copy upload slot per type.
  const [selectedTypes, setSelectedTypes] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)

  // On edit, load the existing record and prefill the form + step state.
  useEffect(() => {
    if (!id) return
    const existing = agreementStore.get(id)
    if (!existing) return

    const rawType = existing.agreementType
    const normalizedTypes: string[] = Array.isArray(rawType)
      ? rawType
      : typeof rawType === 'string' && rawType
      ? [rawType]
      : []

    setSelectedProposal(existing.proposalId)
    setSelectedTypes(normalizedTypes)

    const start = existing.startDate ? dayjs(existing.startDate) : null
    const end = existing.endDate ? dayjs(existing.endDate) : null
    setStartDate(start)
    setEndDate(end)

    // A previously-saved value that isn't one of the three presets (e.g.
    // this agreement was executed on a ₹300 stamp paper) still needs to show
    // up as a real, selected option — not blank just because it's not preset.
    if (existing.stampPaperValue != null) {
      setStampPaperOptions((prev) =>
        prev.some((o) => o.value === existing.stampPaperValue)
          ? prev
          : [...prev, { label: `₹${existing.stampPaperValue}`, value: existing.stampPaperValue as number }].sort((a, b) => a.value - b.value)
      )
    }

    form.setFieldsValue({
      proposalId: existing.proposalId,
      startDate: start,
      endDate: end,
      renewalReminder: existing.renewalReminder,
      stampPaperValue: existing.stampPaperValue,
      stampPaperNumber: existing.stampPaperNumber,
      placeOfExecution: existing.placeOfExecution,
      notes: existing.notes,
      parties: existing.parties ?? [],
      agreementType: normalizedTypes,
      signedCopies: Object.fromEntries(
        (existing.signedCopies ?? []).map((c) => [c.type, (c as any).url || (c as any).fileUrl])
      ),
    })
    setCurrentStep(0)
  }, [id])

  // Helper to resolve party defaults for a given proposal ID
  const resolvePartiesForProposal = (pid: string) => {
    const p = proposals.find(pr => pr.id === pid)
    const lead = p ? leadStore.getLead(p.leadId) : undefined

    const matchedGen = (p || lead)
      ? generators.find(g =>
          (lead && g.name.toLowerCase().trim() === lead.companyName.toLowerCase().trim()) ||
          (lead && g.name.toLowerCase().includes(lead.companyName.toLowerCase())) ||
          (lead && lead.companyName.toLowerCase().includes(g.name.toLowerCase())) ||
          (p && g.name.toLowerCase().includes((p.leadName || '').toLowerCase())) ||
          (lead && (g as any).leadId === lead.id) ||
          (p && (g as any).leadId === p.leadId)
        )
      : undefined

    const matchedCon = (p || lead)
      ? consumers.find(c =>
          (lead && c.name.toLowerCase().trim() === lead.companyName.toLowerCase().trim()) ||
          (lead && c.name.toLowerCase().includes(lead.companyName.toLowerCase())) ||
          (lead && lead.companyName.toLowerCase().includes(c.name.toLowerCase())) ||
          (p && c.name.toLowerCase().includes((p.leadName || '').toLowerCase()))
        )
      : undefined

    if (matchedGen) {
      return [
        {
          role: 'Generator',
          name: matchedGen.name,
          rep: matchedGen.contactPersonName || '',
          designation: matchedGen.designation || '',
          email: matchedGen.contactEmail || lead?.email || '',
          phone: matchedGen.contactMobile || lead?.contactNumber || '',
          address: matchedGen.companyAddress || lead?.address || '',
        },
        {
          role: 'Consumer',
          name: matchedCon?.name || (lead && lead.companyName !== matchedGen.name ? lead.companyName : p?.leadName || 'Consumer'),
          rep: matchedCon?.contactPersonName || lead?.customerName || '',
          email: lead?.email || '',
          phone: lead?.contactNumber || '',
          address: lead?.address || '',
        },
        {
          role: 'Shakthi Power (Facilitator)',
          name: 'Shakthi Power Solutions Pvt Ltd',
          rep: 'Authorized Signatory',
        },
      ]
    }

    return [
      {
        role: 'Client',
        name: p?.leadName || lead?.companyName || '',
        rep: lead?.customerName || '',
        email: lead?.email || '',
        phone: lead?.contactNumber || '',
        address: lead?.address || '',
      },
      {
        role: 'Shakthi Power (Facilitator)',
        name: 'Shakthi Power Solutions Pvt Ltd',
        rep: 'Authorized Signatory',
      },
    ]
  }

  // Pre-fill the proposal when arriving from a proposal's "Create Agreement".
  useEffect(() => {
    const pid = (location.state as any)?.proposalId
    if (pid && !isEditing) {
      setSelectedProposal(pid)
      form.setFieldsValue({
        proposalId: pid,
        parties: resolvePartiesForProposal(pid),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposals])

  const selectedProposalData = proposalOptions.find((p) => p.value === selectedProposal)

  const validityDays =
    startDate && endDate ? endDate.diff(startDate, 'day') : null
  const validityYears =
    validityDays !== null ? (validityDays / 365).toFixed(1) : null

  const handleProposalChange = (val: string) => {
    setSelectedProposal(val)
    form.setFieldsValue({
      proposalId: val,
      parties: resolvePartiesForProposal(val),
    })
  }

  const checkDuplicateAgreement = (proposalId: string, parties: any[], currentId?: string): { isDuplicate: boolean; msg?: string } => {
    const allAgreements = agreementStore.getAll()

    // PRIMARY CHECK: same proposal ID = same parties = duplicate
    if (proposalId) {
      const existingByProposal = allAgreements.find(a => {
        if (currentId && (a.id === currentId || a.code === currentId)) return false
        return a.proposalId === proposalId
      })
      if (existingByProposal) {
        return {
          isDuplicate: true,
          msg: `An agreement already exists for this proposal (Code: ${existingByProposal.code || existingByProposal.id}). You cannot create duplicate agreements for the same proposal.`
        }
      }
    }

    // SECONDARY CHECK: same party names (any roles) between both agreements
    const partyNames = (Array.isArray(parties) ? parties : [])
      .map(p => p?.name?.trim().toLowerCase())
      .filter(Boolean)
      .sort()

    if (partyNames.length >= 2) {
      const existingByParties = allAgreements.find(a => {
        if (currentId && (a.id === currentId || a.code === currentId)) return false
        const aNames = (Array.isArray(a.parties) ? a.parties : [])
          .map((p: any) => p?.name?.trim().toLowerCase())
          .filter(Boolean)
          .sort()
        if (aNames.length < 2 || aNames.length !== partyNames.length) return false
        return partyNames.every((n, i) => n === aNames[i])
      })
      if (existingByParties) {
        return {
          isDuplicate: true,
          msg: `An agreement already exists between the same parties (Code: ${existingByParties.code || existingByParties.id}). Duplicate agreements are not allowed.`
        }
      }
    }

    return { isDuplicate: false }
  }

  const handleNext = () => {
    if (currentStep === 0) {
      if (!selectedProposal) {
        message.warning('Please select a proposal first')
        return
      }
      // Check for duplicate by proposal ID immediately on Step 1 → Step 2
      const dupCheck = checkDuplicateAgreement(selectedProposal, [], isEditing && id ? id : undefined)
      if (dupCheck.isDuplicate) {
        message.error(dupCheck.msg)
        return
      }
    }
    if (currentStep === 1) {
      const parties = form.getFieldValue('parties') || []
      const proposalId = form.getFieldValue('proposalId') || selectedProposal || ''
      const dupCheck = checkDuplicateAgreement(proposalId, parties, isEditing && id ? id : undefined)
      if (dupCheck.isDuplicate) {
        message.error(dupCheck.msg)
        return
      }
    }
    setCurrentStep((s) => Math.min(s + 1, 2))
  }

  const handleBack = () => setCurrentStep((s) => Math.max(s - 1, 0))

  const buildRecord = (status: string) => {
    const values = form.getFieldsValue(true)
    const today = dayjs().format('YYYY-MM-DD')
    const proposalData = proposalOptions.find(
      (p) => p.value === (values.proposalId ?? selectedProposal)
    )
    const existing = isEditing && id ? agreementStore.get(id) : undefined

    const rawValTypes = values.agreementType
    const types: string[] = Array.isArray(rawValTypes)
      ? rawValTypes
      : typeof rawValTypes === 'string' && rawValTypes
      ? [rawValTypes]
      : selectedTypes
    const signedCopies = collectSignedCopies(values, types, existing?.signedCopies ?? [])
    // "Fully signed" only when every selected agreement type has an upload.
    const allSigned = types.length > 0 && signedCopies.length === types.length

    const finalStatus = (status === 'Ready for Signing' && allSigned) ? 'Active' : status
    const finalSignedAt = allSigned ? today : (isEditing && existing?.signedAt ? existing.signedAt : null)
    // The "primary" signed copy is just the first real uploaded file — no more
    // fabricated /uploads/<id>_signed.pdf placeholder that pointed at nothing.
    const finalSignedCopyUrl = allSigned
      ? (signedCopies[0]?.url || (isEditing ? existing?.signedCopyUrl : undefined))
      : (isEditing && existing?.signedCopyUrl ? existing.signedCopyUrl : undefined)

    return {
      proposalId: values.proposalId ?? selectedProposal ?? '',
      leadId: proposalData?.leadId ?? '',
      agreementType: types,
      templateType: 'Standard',
      status: finalStatus,
      generatedAt: isEditing && existing?.generatedAt ? existing.generatedAt : today,
      signedAt: finalSignedAt,
      startDate: startDate ? startDate.format('YYYY-MM-DD') : today,
      endDate: endDate ? endDate.format('YYYY-MM-DD') : today,
      validityDays: validityDays !== null ? validityDays : (isEditing ? existing?.validityDays : undefined),
      renewalReminder: values.renewalReminder ?? 30,
      stampPaperValue: values.stampPaperValue,
      stampPaperNumber: values.stampPaperNumber,
      placeOfExecution: values.placeOfExecution,
      notes: values.notes,
      partyName: values.parties?.[0]?.name ?? proposalData?.customerName ?? '',
      parties: values.parties ?? [],
      signedCopies,
      signedCopyUrl: finalSignedCopyUrl,
      createdAt: isEditing && existing?.createdAt ? existing.createdAt : today,
      updatedAt: today,
    }
  }

  const handleSaveDraft = async () => {
    setSubmitting(true)
    try {
      let agrId = id
      if (!isEditing) {
        try {
          agrId = await generateCode('agreements')
        } catch (err) {
          agrId = `AG-${Date.now().toString(36).toUpperCase()}`
        }
      }
      const record = buildRecord('Draft')
      const dupCheck = checkDuplicateAgreement(record.proposalId, record.parties, isEditing && id ? id : undefined)
      if (dupCheck.isDuplicate) {
        message.error(dupCheck.msg)
        return
      }
      const code = (isEditing && id ? agreementStore.get(id)?.code : undefined) || agrId
      agreementStore.save({ id: agrId, code, ...record })
      message.success('Agreement saved as draft')
      navigate('/agreements')
    } finally {
      setSubmitting(false)
    }
  }

  const handleSubmit = () => {
    setSubmitting(true)
    form
      .validateFields()
      .then(async () => {
        const values = form.getFieldsValue(true)
        const types: string[] = values.agreementType ?? []

        if (types.length === 0) {
          message.warning('Please select at least one agreement type in Step 1')
          return
        }

        // Uploading the signed copies is NOT required to create the agreement —
        // physical signing usually happens afterwards. buildRecord activates it
        // only when EVERY type has an upload; otherwise it stays "Ready for
        // Signing" so you can upload the remaining copies later (from the list or
        // detail page). This avoids the dead-end where a created agreement had no
        // upload option anywhere.
        const uploaded = new Set(collectSignedCopies(values, types).map((c) => c.type))
        const missing = types.filter((t) => !uploaded.has(t))

        let agrId = id
        if (!isEditing) {
          try {
            agrId = await generateCode('agreements')
          } catch (err) {
            agrId = `AG-${Date.now().toString(36).toUpperCase()}`
          }
        }

        const record = buildRecord('Ready for Signing')
        const dupCheck = checkDuplicateAgreement(record.proposalId, record.parties, isEditing && id ? id : undefined)
        if (dupCheck.isDuplicate) {
          message.error(dupCheck.msg)
          return
        }
        const code = (isEditing && id ? agreementStore.get(id)?.code : undefined) || agrId
        agreementStore.save({ id: agrId, code, ...record })

        if (missing.length) {
          message.success(
            `Agreement saved — upload the signed copy for ${missing.join(', ')} to activate it.`
          )
        } else {
          message.success('Agreement submitted — all signed copies uploaded, now Active.')
        }
        navigate('/agreements')
      })
      .catch(() => {
        message.error('Please fill in all required fields')
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  const cardStyle = (active: boolean): React.CSSProperties => ({
    borderRadius: 10,
    border: active
      ? '2px solid var(--brand-primary)'
      : '1.5px solid var(--border-default)',
    background: active ? 'rgba(0,26,77,0.04)' : 'var(--surface-1)',
    cursor: 'pointer',
    transition: 'all 0.2s',
    height: '100%',
  })

  const stepContent = () => {
    /* ── Step 1: Select Proposal ── */
    if (currentStep === 0) {
      return (
        <div>
          <h3 style={sectionTitle}>Select Proposal</h3>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={14} lg={10}>
              <Form.Item label="Proposal" name="proposalId" rules={[{ required: true, message: 'Please select a proposal' }]}>
                <Select
                  size="large"
                  placeholder="Select a proposal…"
                  options={proposalOptions.map((p) => ({ label: p.label, value: p.value }))}
                  onChange={handleProposalChange}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={10} lg={8}>
              <Form.Item label="Agreement Type" name="agreementType" rules={[{ required: true, message: 'Please select an agreement type' }]}>
                <Select
                  mode="multiple"
                  size="large"
                  showSearch
                  placeholder="e.g. Power Purchase Agreement"
                  options={agreementTypeOptions}
                  onChange={(vals: string[]) => setSelectedTypes(vals)}
                />
              </Form.Item>
            </Col>
          </Row>

          {selectedProposalData && (
            <Card
              style={{
                background: 'rgba(0,153,204,0.06)',
                borderRadius: 10,
                border: '1.5px solid rgba(0,153,204,0.3)',
                marginTop: 4,
              }}
              styles={{ body: { padding: '14px 20px' } }}
            >
              <div style={{ fontWeight: 700, color: 'var(--brand-primary)', marginBottom: 8, fontSize: 14 }}>
                Auto-filled from Proposal {selectedProposalData.value}
              </div>
              <Row gutter={[24, 8]}>
                {[
                  { label: 'Customer', value: selectedProposalData.customerName },
                  { label: 'Lead ID', value: selectedProposalData.leadId },
                  { label: 'Tariff Rate', value: selectedProposalData.tariff },
                  { label: 'Location', value: selectedProposalData.location },
                ].map((item) => (
                  <Col key={item.label} xs={24} sm={12} lg={6}>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 2 }}>
                      {item.label}
                    </div>
                    <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 13 }}>
                      {item.value}
                    </div>
                  </Col>
                ))}
              </Row>
            </Card>
          )}
        </div>
      )
    }

    /* ── Step 2: Configure Parties ── */
    if (currentStep === 1) {
      return (
        <div>
          <h3 style={sectionTitle}>Configure Parties</h3>
          <Row gutter={[16, 16]}>
          <Form.List name="parties" initialValue={[{ role: 'Client', name: selectedProposalData?.customerName || '' }, { role: 'Shakthi Power (Facilitator)', name: 'Shakthi Power Solutions Pvt Ltd', rep: 'Authorized Signatory' }]}>
            {(fields, { add, remove }) => (
              <>
                <Row gutter={[16, 16]}>
                  {fields.map(({ key, name, ...restField }, idx) => (
                    <Col key={key} xs={24} lg={12}>
                      <Card
                        title={
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontWeight: 700, color: 'var(--brand-primary)' }}>
                              Party {idx + 1}
                            </span>
                            {fields.length > 1 && (
                              <Button type="link" danger size="small" onClick={() => remove(name)}>Remove</Button>
                            )}
                          </div>
                        }
                        style={{ borderRadius: 10, boxShadow: 'var(--shadow-sm)', height: '100%' }}
                        styles={{ body: { padding: '16px' } }}
                      >
                        <Row gutter={[12, 0]}>
                          <Col xs={24} sm={12}>
                            <Form.Item
                              {...restField}
                              name={[name, 'role']}
                              label="Role"
                              rules={[{ required: true, message: 'Required' }]}
                              style={{ marginBottom: 12 }}
                            >
                              <Select
                                placeholder="e.g. Client, Generator, Consumer..."
                                onChange={(val) => {
                                  if (val === 'Generator') {
                                    const currentPartyName = form.getFieldValue(['parties', name, 'name'])
                                    const gen = generators.find(g => g.name === currentPartyName) || generators[0]
                                    if (gen) {
                                      const parties = form.getFieldValue('parties') || []
                                      parties[name] = {
                                        ...parties[name],
                                        role: 'Generator',
                                        name: gen.name,
                                        rep: gen.contactPersonName || '',
                                        designation: gen.designation || '',
                                        email: gen.contactEmail || '',
                                        phone: gen.contactMobile || '',
                                        address: gen.companyAddress || '',
                                      }
                                      form.setFieldsValue({ parties: [...parties] })
                                    }
                                  }
                                }}
                                options={[
                                  { value: 'Client', label: 'Client' },
                                  { value: 'Generator', label: 'Generator' },
                                  { value: 'Consumer', label: 'Consumer' },
                                  { value: 'Investor', label: 'Investor' },
                                  { value: 'Shakthi Power (Facilitator)', label: 'Shakthi Power (Facilitator)' },
                                  { value: 'Other', label: 'Other' }
                                ]}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={12}>
                            <Form.Item
                              noStyle
                              shouldUpdate={(prevValues, currentValues) =>
                                prevValues.parties?.[name]?.role !== currentValues.parties?.[name]?.role
                              }
                            >
                              {() => {
                                const currentRole = form.getFieldValue(['parties', name, 'role'])
                                if (currentRole === 'Generator') {
                                  return (
                                    <Form.Item
                                      {...restField}
                                      name={[name, 'name']}
                                      label="Generator Name"
                                      rules={[{ required: true, message: 'Required' }]}
                                      style={{ marginBottom: 12 }}
                                    >
                                      <Select
                                        showSearch
                                        placeholder="Select Generator from Master"
                                        options={generators.map(g => ({ label: `${g.name} (${g.code || 'Gen'})`, value: g.name }))}
                                        onChange={(genName) => {
                                          const gen = generators.find(g => g.name === genName)
                                          if (gen) {
                                            const parties = form.getFieldValue('parties') || []
                                            parties[name] = {
                                              ...parties[name],
                                              name: gen.name,
                                              rep: gen.contactPersonName || parties[name]?.rep || '',
                                              designation: gen.designation || parties[name]?.designation || '',
                                              email: gen.contactEmail || parties[name]?.email || '',
                                              phone: gen.contactMobile || parties[name]?.phone || '',
                                              address: gen.companyAddress || parties[name]?.address || '',
                                            }
                                            form.setFieldsValue({ parties: [...parties] })
                                          }
                                        }}
                                      />
                                    </Form.Item>
                                  )
                                }
                                return (
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'name']}
                                    label="Party Name"
                                    rules={[{ required: true, message: 'Required' }]}
                                    style={{ marginBottom: 12 }}
                                  >
                                    <Input placeholder="Enter party name" />
                                  </Form.Item>
                                )
                              }}
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={12}>
                            <Form.Item
                              {...restField}
                              name={[name, 'rep']}
                              label="Representative Name"
                              style={{ marginBottom: 12 }}
                            >
                              <Input placeholder="Representative name" />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={12}>
                            <Form.Item {...restField} name={[name, 'designation']} label="Designation" style={{ marginBottom: 12 }}>
                              <Input placeholder="Designation" />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={12}>
                            <Form.Item
                              {...restField}
                              name={[name, 'email']}
                              label="Contact Email"
                              rules={[{ type: 'email', message: 'Enter valid email' }]}
                              style={{ marginBottom: 12 }}
                            >
                              <Input type="email" placeholder="email@example.com" />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={12}>
                            <Form.Item {...restField} name={[name, 'phone']} label="Contact Phone" style={{ marginBottom: 12 }}>
                              <Input placeholder="+91 XXXXX XXXXX" />
                            </Form.Item>
                          </Col>
                          <Col xs={24}>
                            <Form.Item {...restField} name={[name, 'address']} label="Address" style={{ marginBottom: 0 }}>
                              <Input.TextArea rows={1} placeholder="Full address" />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Card>
                    </Col>
                  ))}
                </Row>
                <Button type="dashed" onClick={() => add()} block style={{ marginTop: 16 }}>
                  + Add Party
                </Button>
              </>
            )}
          </Form.List>
          </Row>
        </div>
      )
    }

    /* ── Step 4: Contract Terms & Upload ── */
    return (
      <div>
        <h3 style={sectionTitle}>Contract Terms &amp; Upload</h3>
        <Row gutter={[24, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="startDate"
              label="Agreement Date"
              rules={[{ required: true, message: 'Required' }]}
            >
              <DatePicker
                size="large"
                style={{ width: '100%' }}
                onChange={(val) => setStartDate(val)}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="endDate"
              label="Valid Date"
              rules={[{ required: true, message: 'Required' }]}
            >
              <DatePicker
                size="large"
                style={{ width: '100%' }}
                onChange={(val) => setEndDate(val)}
              />
            </Form.Item>
          </Col>

          {validityDays !== null && (
            <Col xs={24}>
              <div
                style={{
                  padding: '10px 16px',
                  background: 'rgba(0,166,81,0.07)',
                  borderRadius: 8,
                  marginBottom: 16,
                  color: 'var(--brand-green)',
                  fontWeight: 600,
                  fontSize: 13,
                }}
              >
                Validity Period: {validityYears} years / {validityDays} days
              </div>
            </Col>
          )}

          <Col xs={24} sm={12}>
            <Form.Item
              name="renewalReminder"
              label="Send renewal reminder X days before expiry"
              initialValue={30}
            >
              <InputNumber min={0} max={365} size="large" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="stampPaperValue" label="Stamp Paper Value">
              <Select
                size="large"
                placeholder="Select or type a value, e.g. 300"
                showSearch
                searchValue={stampPaperSearch}
                onSearch={setStampPaperSearch}
                filterOption={(input, option) =>
                  String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={
                  /^\d+$/.test(stampPaperSearch) && !stampPaperOptions.some((o) => o.value === Number(stampPaperSearch))
                    ? [...stampPaperOptions, { label: `₹${stampPaperSearch} (new)`, value: Number(stampPaperSearch) }]
                    : stampPaperOptions
                }
                onChange={(val: number) => {
                  setStampPaperSearch('')
                  setStampPaperOptions((prev) =>
                    prev.some((o) => o.value === val) ? prev : [...prev, { label: `₹${val}`, value: val }].sort((a, b) => a.value - b.value)
                  )
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="stampPaperNumber" label="Stamp Paper Number">
              <Input size="large" placeholder="Stamp paper reference number" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="placeOfExecution" label="Place of Execution">
              <Input size="large" placeholder="e.g. Coimbatore, Tamil Nadu" />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item name="notes" label="Notes / Special Clauses">
              <Input.TextArea rows={3} placeholder="Any special conditions or clauses…" />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <div style={{ fontWeight: 700, color: 'var(--brand-primary)', marginBottom: 4, fontSize: 15 }}>
              Upload Signed Copies
            </div>
            {selectedTypes.length === 0 ? (
              <Alert
                type="warning"
                showIcon
                message="Go back to Step 1 and select at least one agreement type to upload its signed copy."
                style={{ marginBottom: 12 }}
              />
            ) : (
              <>
                <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: '0 0 14px' }}>
                  Upload the signed stamp-paper copy for <strong>each</strong> selected agreement —
                  all {selectedTypes.length} are required before you can submit.
                </p>
                {selectedTypes.map((type) => (
                  <Form.Item
                    key={type}
                    name={['signedCopies', type]}
                    label={`Signed Copy — ${type}`}
                    style={{ marginBottom: 12 }}
                  >
                    <DocumentUploader folder="agreements" accept=".pdf,.jpg,.jpeg,.png" />
                  </Form.Item>
                ))}
              </>
            )}
          </Col>
        </Row>
      </div>
    )
  }

  const sectionTitle: React.CSSProperties = {
    fontSize: 17,
    fontWeight: 700,
    color: 'var(--brand-primary)',
    marginBottom: 20,
    marginTop: 0,
  }

  return (
    <div style={{ padding: '24px', background: 'var(--surface-0)', minHeight: '100vh' }}>
      {/* Page Header */}
      <div style={{ marginBottom: 24 }}>
        <h2
          style={{
            margin: 0,
            fontSize: 26,
            fontWeight: 700,
            color: 'var(--brand-primary)',
          }}
        >
          {isEditing ? 'Edit Agreement' : 'Create New Agreement'}
        </h2>
        <p style={{ margin: '4px 0 0', color: 'var(--text-secondary)', fontSize: 14 }}>
          {isEditing
            ? 'Update the agreement details below'
            : 'Follow the steps to create a new power purchase agreement'}
        </p>
      </div>

      {/* Steps indicator */}
      <Card
        style={{
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-sm)',
          background: 'var(--surface-1)',
          marginBottom: 16,
        }}
        styles={{ body: { padding: '20px 24px' } }}
      >
        <Steps
          current={currentStep}
          onChange={(step) => setCurrentStep(step)}
          items={[
            { title: 'Select Proposal' },
            { title: 'Configure Parties' },
            { title: 'Contract Terms & Upload' },
          ]}
        />
      </Card>

      {/* Step content */}
      <Card
        style={{
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-sm)',
          background: 'var(--surface-1)',
          marginBottom: 16,
        }}
        styles={{ body: { padding: '28px 28px 20px' } }}
      >
        <Form form={form} layout="vertical" autoComplete="off">
          {stepContent()}
        </Form>
      </Card>

      {/* Footer navigation */}
      <Card
        style={{
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-sm)',
          background: 'var(--surface-1)',
        }}
        styles={{ body: { padding: '16px 24px' } }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            size="large"
            onClick={() => navigate('/agreements')}
          >
            Cancel
          </Button>

          <Space>
            {currentStep > 0 && (
              <Button size="large" onClick={handleBack}>
                ← Back
              </Button>
            )}

            {currentStep < 2 && (
              <Button
                type="primary"
                size="large"
                onClick={handleNext}
                disabled={currentStep === 0 && !selectedProposal}
                style={{ background: 'var(--brand-primary)', borderColor: 'var(--brand-primary)' }}
              >
                Next →
              </Button>
            )}

            {currentStep === 2 && (
              <>
                <Button size="large" onClick={handleSaveDraft} loading={submitting} disabled={submitting}>
                  Save as Draft
                </Button>
                <Button
                  type="primary"
                  size="large"
                  icon={<PlusOutlined />}
                  onClick={handleSubmit}
                  loading={submitting}
                  disabled={submitting}
                  style={{ background: 'var(--brand-green)', borderColor: 'var(--brand-green)' }}
                >
                  Submit Agreement
                </Button>
              </>
            )}
          </Space>
        </div>
      </Card>
    </div>
  )
}

export default AgreementForm
